import type { EventCalendarCardProps } from '4-sections/EventCalendar/EventsWrapper/EventCalendarCard';
import { TYPES } from '4-sections/EventCalendar/libs/constants';
import { type EventCalendarQuery } from 'cms/cms';
import { format } from 'date-fns';

type EventPage = NonNullable<EventCalendarQuery['EventPage']>;
type EventPageItems = NonNullable<EventPage['items']>;
type FunctionPage = NonNullable<EventCalendarQuery['FunctionPage']>;
type FunctionPageItems = NonNullable<FunctionPage['items']>;
type ExperienceBlock = NonNullable<EventCalendarQuery['ExperienceBlock']>;
type ExperienceBlockItems = NonNullable<ExperienceBlock['items']>;

const DEFAULT_TBC_TEXT = 'Opening hours to be confirmed';

export interface EventCalendarData {
    eventCalendarCards: EventCalendarCardProps[];
}

// Helper function to identify SportsEvent types
const isSportsEvent = (expanded: any): boolean => { // eslint-disable-line @typescript-eslint/no-explicit-any
    return expanded?.__typename === 'SportsEvent';
};

// Helper function to extract team names from event title (fallback)
const extractTeamNamesFromTitle = (eventName: string): { team1?: string; team2?: string } => {
    const teamMatch = eventName.match(/^(.+?)\s+v\s+(.+?)(?:\s+.*)?$/i);
    if (teamMatch) {
        return {
            team1: teamMatch[1]?.trim(),
            team2: teamMatch[2]?.trim(),
        };
    }
    return {};
};

// Extracts CalendarTags as an array of strings for custom filtering
const extractCalendarTags = (calendarTags: any[]): string[] => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!calendarTags || !Array.isArray(calendarTags)) {
        return [];
    }

    return calendarTags
        .map(tag => {
            return tag?.Name || tag?.Description;
        })
        .filter(Boolean) as string[];
};

// Extracts team names for sports events using multiple fallback strategies
const getTeamNames = (expanded: any, sportsTeamLookup: Map<number, string>): { team1?: string; team2?: string } => { // eslint-disable-line @typescript-eslint/no-explicit-any
    let team1Name: string | undefined;
    let team2Name: string | undefined;

    // Strategy 1: Lookup from SportsTeam data
    const team1Id = expanded.Team1?.Id;
    const team2Id = expanded.Team2?.Id;

    if (team1Id && sportsTeamLookup.has(team1Id)) {
        team1Name = sportsTeamLookup.get(team1Id);
    }
    if (team2Id && sportsTeamLookup.has(team2Id)) {
        team2Name = sportsTeamLookup.get(team2Id);
    }

    // Strategy 2: Extract from expanded team data
    if (!team1Name && expanded.Team1?.Expanded?.Name) {
        team1Name = expanded.Team1.Expanded.Name;
    }
    if (!team2Name && expanded.Team2?.Expanded?.Name) {
        team2Name = expanded.Team2.Expanded.Name;
    }

    // Strategy 3: Parse from event name (fallback)
    if (!team1Name || !team2Name) {
        const { team1: fallbackTeam1, team2: fallbackTeam2 } = extractTeamNamesFromTitle(expanded.EventName || '');
        team1Name = team1Name || fallbackTeam1;
        team2Name = team2Name || fallbackTeam2;
    }

    return { team1: team1Name, team2: team2Name };
};

/**
 * Resolves EventPage items into calendar cards
 * Handles both regular events and sports events with team information
 */
const resolveEventPageCards = (
    items: EventPageItems,
    sportsTeamLookup: Map<number, string>,
    sportLookup: Map<number, string>
): EventCalendarCardProps[] => {
    if (!items) {
        return [];
    }

    const processedEvents = items.map(item => {
        if (!item?.Event?.Expanded) {
            return null;
        }

        const expanded = item.Event.Expanded as any; // eslint-disable-line @typescript-eslint/no-explicit-any

        // Check if this item has EventDate
        if (!expanded.EventDate) {
            return null;
        }

        const { EventName, EventType, EventTypeLabel, EventDate, EventCategory } = expanded;

        // Determine event type (sport name for sports events, otherwise event type)
        // let typeDescription = '';
        const cardLabel = EventTypeLabel && EventTypeLabel.length > 0
            ? EventTypeLabel[0]?.Description || EventTypeLabel[0]?.Name || ''
            : '';
        const dataEventType = EventType && EventType.length > 0 ? EventType[0]?.Description || EventType[0]?.Name || '' : '';

        // Extract category information
        const categoryDescription = EventCategory && EventCategory.length > 0
            ? EventCategory[0]?.Description || EventCategory[0]?.Name || ''
            : '';

        // Extract CalendarTags for custom filtering
        const calendarTags = extractCalendarTags(item.CalendarTags || []);

        const baseEvent: EventCalendarCardProps = {
            url: item.Url || '#',
            title: EventName || '',
            tagLabel: cardLabel,
            type: dataEventType,
            cardIcon: dataEventType,
            category: categoryDescription,
            cardType: 'event',
            date: EventDate as string | number,
            calendarTags,
        };

        // Add team information for sports events
        if (isSportsEvent(expanded)) {
            const { team1, team2 } = getTeamNames(expanded, sportsTeamLookup);
            return { ...baseEvent, team1, team2 };
        }

        return baseEvent;
    }).filter(Boolean) as EventCalendarCardProps[];

    return processedEvents;
};

/**
 * Resolves FunctionPage items into calendar cards
 * Functions are club events
 */
const resolveFunctionPageCards = (items: FunctionPageItems): EventCalendarCardProps[] => {
    if (!items) {
        return [];
    }

    const processedFunctions = items.map(item => {
        if (!item?.Url || !item?.Name || !item?.FunctionDate) {
            return null;
        }

        // Extract CalendarTags for custom filtering
        const calendarTags = extractCalendarTags(item.CalendarTags || []);

        return {
            url: item.Url,
            title: item.FunctionName || item.Name,
            type: TYPES.CLUB_FUNCTION,
            tagLabel: TYPES.CLUB_FUNCTION,
            cardIcon: TYPES.CLUB_FUNCTION,
            category: TYPES.CLUB_FUNCTION,
            cardType: 'club-function',
            date: item.FunctionDate,
            calendarTags,
        };
    }).filter(Boolean) as EventCalendarCardProps[];

    return processedFunctions;
};

// Helper functions for ExperienceBlock processing
const getDayName = (date: Date): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
};

const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).replace(/\s([ap]m)$/, '$1'); // Removes any whitespace before AM/PM
};

// Helper function to find matching temporary hours for a given date and temporary hour list
// Returns the LAST matching entry to allow later entries to override earlier ones
const findMatchingTemporaryHours = (
    currentDate: Date,
    temporaryHourList: any[], // eslint-disable-line @typescript-eslint/no-explicit-any
    temporaryHoursLookup: Map<number, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): any => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!temporaryHourList || !Array.isArray(temporaryHourList)) {
        return null;
    }

    let lastMatchingTempHours = null;

    // Find ALL matching temporary hours, keeping the last one found (allows later entries to override)
    for (const tempHourItem of temporaryHourList) {
        // First try to use the expanded data directly from the GraphQL response
        if (tempHourItem?.ContentLink?.Expanded) {
            const tempHourData = tempHourItem.ContentLink.Expanded;

            // Check if current date is within the temporary hours date range
            const fromDate = tempHourData.FromDate ? new Date(tempHourData.FromDate) : null;
            const toDate = tempHourData.ToDate ? new Date(tempHourData.ToDate) : null;

            // const isSingleDay = fromDate && toDate && fromDate.getTime() === toDate.getTime();
            // console.log('🚀 ~ isSingleDay:', isSingleDay);

            const isWithinDateRange = (!fromDate || currentDate >= fromDate) && (!toDate || currentDate <= toDate);

            if (isWithinDateRange) {
                lastMatchingTempHours = tempHourData; // Keep this match, but continue looking for later ones
            }
        } else {
            // Fallback to lookup table if expanded data is not available
            const tempHourId = tempHourItem?.ContentLink?.Id;
            if (tempHourId) {
                const tempHourData = temporaryHoursLookup.get(tempHourId);
                if (tempHourData) {
                    // Check if current date is within the temporary hours date range
                    const fromDate = tempHourData.FromDate ? new Date(tempHourData.FromDate) : null;
                    const toDate = tempHourData.ToDate ? new Date(tempHourData.ToDate) : null;

                    const isWithinDateRange = (!fromDate || currentDate >= fromDate) &&
                                             (!toDate || currentDate <= toDate);

                    if (isWithinDateRange) {
                        lastMatchingTempHours = tempHourData; // Keep this match, but continue looking for later ones
                    }
                }
            }
        }
    }

    return lastMatchingTempHours;
};

// Helper function to format temporary hours description
const formatTemporaryHours = (tempHours: any): { primary: string; secondary?: string } => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!tempHours) {
        return { primary: '' };
    }

    const { OpeningStatus, PrimaryLabel, PrimaryDailyOpen, PrimaryDailyClose, SecondaryLabel, SecondaryDailyOpen, SecondaryDailyClose } = tempHours;

    // Handle closed status (early return only for closed)
    if (OpeningStatus === 'closed' && !PrimaryLabel && !SecondaryLabel) {
        return { primary: 'Closed' };
    }

    let primaryDescription = '';
    let secondaryDescription = '';

    // Primary hours
    if (PrimaryLabel) {
        if (PrimaryDailyOpen && PrimaryDailyClose) {
            primaryDescription = `${PrimaryLabel} ${formatTime(PrimaryDailyOpen)} to ${formatTime(PrimaryDailyClose)}`;
        } else if (PrimaryDailyOpen && !PrimaryDailyClose) {
            primaryDescription = `${PrimaryLabel} from ${formatTime(PrimaryDailyOpen)}`;
        } else {
            primaryDescription = PrimaryLabel;
        }
    }

    // Secondary hours
    if (SecondaryLabel) {
        if (SecondaryDailyOpen && SecondaryDailyClose) {
            secondaryDescription = `${SecondaryLabel} ${formatTime(SecondaryDailyOpen)} to ${formatTime(SecondaryDailyClose)}`;
        } else if (SecondaryDailyOpen) {
            secondaryDescription = `${SecondaryLabel} from ${formatTime(SecondaryDailyOpen)}`;
        } else {
            secondaryDescription = SecondaryLabel;
        }
    }

    // If we have no primary description but it's TBC, use that as fallback
    if (!primaryDescription && OpeningStatus === 'tbc') {
        primaryDescription = DEFAULT_TBC_TEXT;
    }

    // If we have no primary description and it's open, use that as fallback
    if (!primaryDescription && OpeningStatus === 'open') {
        primaryDescription = 'Open';
    }

    return {
        primary: primaryDescription,
        secondary: secondaryDescription || undefined,
    };
};

const getOpeningHours = (standardHours: any, dayName: string): string => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!standardHours) {
        return '';
    }

    const openField = `${dayName}Open`;
    const closeField = `${dayName}Close`;
    const statusField = `${dayName}Status`;
    const openTime = standardHours[openField];
    const closeTime = standardHours[closeField];
    const dayStatus = standardHours[statusField];

    const hasNoHours = !openTime && !closeTime && dayStatus === 'open';
    const isFullyTBC = !openTime && !closeTime && !standardHours.Label && dayStatus === 'tbc';
    const isTBCWithLabel = !openTime && !closeTime && dayStatus === 'tbc' && !!standardHours.Label;

    if (hasNoHours) {
        return standardHours.Label ? standardHours.Label : 'Open';
    }

    if (dayStatus === 'closed') {
        return 'Closed';
    }

    if (isFullyTBC) {
        return DEFAULT_TBC_TEXT;
    }

    if (isTBCWithLabel) {
        return standardHours.Label;
    }

    const label = standardHours.Label || 'Open from';
    const hoursText = `${formatTime(openTime)} to ${formatTime(closeTime)}`;

    return `${label} ${hoursText}`;
};

/**
 * Resolves ExperienceBlock items into calendar cards
 * Handles date range expansion and opening hours display with temporary hours override
 */
const resolveExperienceBlockCards = (
    items: ExperienceBlockItems,
    temporaryHoursLookup: Map<number, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    sectionBrands: string[]
): EventCalendarCardProps[] => {
    if (!items) {
        return [];
    }

    const processedExperiences: EventCalendarCardProps[] = [];

    items.forEach(item => {
        if (!item?.Name) {
            return;
        }

        const itemBrands = item.Brands?.split(',').map((brand: string) => {
            return brand.trim();
        }) ?? [];

        const isBrandMatch = sectionBrands.length !== 0 && itemBrands.some((brand: string) => {
            return sectionBrands.includes(brand);
        });

        if (!isBrandMatch) {
            return;
        }

        // Extract category from CalendarTags
        const categoryDescription = item.CalendarTags && item.CalendarTags.length > 0
            ? item.CalendarTags[0]?.Description || item.CalendarTags[0]?.Name || ''
            : '';

        // Extract CalendarTags for custom filtering
        const calendarTags = extractCalendarTags(item.CalendarTags || []);

        // Use the provided Type or fallback to 'Experience'
        const cardIcon = item.Type || TYPES.EXPERIENCES;

        const createExperienceCard = (date: string): EventCalendarCardProps | null => {
            const currentDate = new Date(date);

            // Check for temporary hours that override standard hours
            const matchingTempHours = findMatchingTemporaryHours(
                currentDate,
                item.TemporaryHourList || [],
                temporaryHoursLookup
            );

            let openingHours: string;
            let secondaryOpeningHours: string | undefined;
            let itemStatus: string | undefined;

            if (matchingTempHours) {
                // Use temporary hours if available and within date range
                const tempHours = formatTemporaryHours(matchingTempHours);
                openingHours = tempHours.primary;
                secondaryOpeningHours = tempHours.secondary;
                itemStatus = matchingTempHours.OpeningStatus;
            } else {
                // Fall back to standard hours
                const dayName = getDayName(currentDate);
                openingHours = getOpeningHours(item.StandardHours, dayName);
                secondaryOpeningHours = undefined;
                // Try to get status from standard hours if available
                const statusKey = `${dayName}Status` as keyof typeof item.StandardHours;
                if (item.StandardHours?.[statusKey]) {
                    itemStatus = item.StandardHours[statusKey] as string | undefined;
                }
            }
            // Skip card if status is 'hide'
            if (itemStatus === 'hide') {
                return null;
            }

            let itemUrl = item.Url || '#';

            if (item.ActionLink?.Href) {
                try {
                    const urlObj = new URL(item.ActionLink.Href, window?.location?.origin || 'http://localhost:3000');
                    if (item.ActionLinkAppendDate) {
                        const dateParam = format(currentDate, 'yyyy-MM-dd');
                        urlObj.searchParams.set('date', dateParam);
                    }
                    itemUrl = urlObj.toString();
                } catch {
                    // fallback if URL constructor fails
                    itemUrl = item.ActionLink.Href;
                }
            }

            const cardData = {
                url: itemUrl,
                title: item.Name || '',
                tagLabel: '',
                type: TYPES.EXPERIENCES,
                cardIcon,
                category: categoryDescription || TYPES.EXPERIENCES,
                cardType: 'experience',
                date: currentDate.toISOString(),
                description: openingHours,
                secondaryDescription: secondaryOpeningHours,
                calendarTags,
                status: itemStatus,
            };

            return cardData;
        };

        item.StandardHours = item.StandardHours || {};

        // Handle different date scenarios
        if (item.GlobalStartDate && item.GlobalEndDate) {
            // Date range: create cards for each day
            const startDate = new Date(item.GlobalStartDate);
            const endDate = new Date(item.GlobalEndDate);
            const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            for (let i = 0; i <= diffDays; i += 1) {
                const currentDate = new Date(startDate);
                currentDate.setDate(startDate.getDate() + i);
                const card = createExperienceCard(currentDate.toISOString());
                if (card) {
                    processedExperiences.push(card);
                }
            }
        } else if (item.GlobalStartDate) {
            // Single start date
            const card = createExperienceCard(item.GlobalStartDate);
            if (card) {
                processedExperiences.push(card);
            }
        } else {
            // Fallback to current date
            const card = createExperienceCard(new Date().toISOString());
            if (card) {
                processedExperiences.push(card);
            }
        }
    });

    return processedExperiences;
};

/**
 * Main resolver function for the Event Calendar component.
 * Transforms CMS data from multiple content types into a unified EventCalendarData structure.
 *
 * @param data - GraphQL response containing EventPage, FunctionPage, ExperienceBlock, SportsTeam, and EventSport data
 * @param dataTypes - Optional array of content types to include (e.g., ['Events', 'Functions', 'Experiences'])
 *                   If not provided, all content types are included
 * @returns EventCalendarData with sorted cards, or null if no cards found
 */
export const resolveEventCalendar = (data: EventCalendarQuery, dataTypes?: string[], brands?: string[]): EventCalendarData | null => {
    // Create a lookup table for SportsTeam data
    const sportsTeamLookup = new Map<number, string>();

    // Create a lookup table for EventSport data
    const sportLookup = new Map<number, string>();

    // Create a lookup table for TemporaryHoursBlock data
    const temporaryHoursLookup = new Map<number, any>(); // eslint-disable-line @typescript-eslint/no-explicit-any

    if (data.SportsTeam?.items) {
        data.SportsTeam.items.forEach(team => {
            if (team?.ContentLink?.Id && team.Name) {
                sportsTeamLookup.set(team.ContentLink.Id, team.Name);
            }
        });
    }

    if (data.EventSport?.items) {
        data.EventSport.items.forEach(sport => {
            if (sport?.ContentLink?.Id && (sport.SportName || sport.Name)) {
                const sportName = sport.SportName || sport.Name || '';
                sportLookup.set(sport.ContentLink.Id, sportName);
            }
        });
    }

    // Build temporary hours lookup from TemporaryHoursBlock data
    if (data.TemporaryHoursBlock?.items) {
        data.TemporaryHoursBlock.items.forEach(tempHours => {
            if (tempHours?.ContentLink?.Id) {
                temporaryHoursLookup.set(tempHours.ContentLink.Id, tempHours);
            }
        });
    }

    // Process all content types
    const eventCards: EventCalendarCardProps[] = [];

    // Add EventPage items (if "Events" is in dataTypes or dataTypes is not specified)
    if (data.EventPage?.items && (!dataTypes || dataTypes.includes('Events'))) {
        const eventPageCards = resolveEventPageCards(data.EventPage.items, sportsTeamLookup, sportLookup);
        eventCards.push(...eventPageCards);
    }

    // Add FunctionPage items (if "Functions" is in dataTypes or dataTypes is not specified)
    if (data.FunctionPage?.items && (!dataTypes || dataTypes.includes('Functions'))) {
        const functionPageCards = resolveFunctionPageCards(data.FunctionPage.items);
        eventCards.push(...functionPageCards);
    }

    // Add ExperienceBlock items (if "Experiences" is in dataTypes or dataTypes is not specified)
    if (data.ExperienceBlock?.items && (!dataTypes || dataTypes.includes('Experiences'))) {
        const experienceBlockCards = resolveExperienceBlockCards(data.ExperienceBlock.items, temporaryHoursLookup, brands || []);
        eventCards.push(...experienceBlockCards);
    }

    // Return null if no cards were found
    if (eventCards.length === 0) {
        return null;
    }

    // Sort by date
    eventCards.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
    });

    return { eventCalendarCards: eventCards };
};
